import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import BookingModal from "@/components/BookingModal";
import { serviceCategories, bookableServices } from "@/data/serviceCatalog";

export default function BookingPage() {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [activeCategory, setActiveCategory] = useState(serviceCategories[0]?.id || "");
  const [expandedServiceId, setExpandedServiceId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentState = params.get("payment");
    const sessionId = params.get("session_id");
    const token = params.get("token");

    if (paymentState === "cancelled") {
      toast.error("Payment was cancelled. Booking was not created.");
      window.history.replaceState({}, "", "/book");
      return;
    }

    if (paymentState === "success" && sessionId && token) {
      navigate(
        `/booking-confirmation?session_id=${encodeURIComponent(sessionId)}&token=${encodeURIComponent(token)}`,
        { replace: true }
      );
      return;
    }
  }, [location.search, navigate]);

  const openBooking = (serviceId) => {
    setSelectedServiceId(serviceId);
    setIsBookingOpen(true);
  };

  const activeCategoryData =
    serviceCategories.find((category) => category.id === activeCategory) || serviceCategories[0];

  const activeServices = activeCategoryData
    ? bookableServices.filter((service) => service.categoryId === activeCategoryData.id)
    : [];

  return (
    <div className="bg-[#F0F0F0] min-h-screen">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#666] hover:text-[#111] transition-colors mb-6"
        >
          <ArrowLeft size={16} /> Back to Home
        </Link>
        <div className="text-center">
          <h1 className="script-heading text-3xl md:text-4xl mb-4">
            Book Your Appointment
          </h1>
          <p className="text-[#666] max-w-2xl mx-auto">
            Choose a category, select your exact service, and continue to secure checkout.
            You can expand each service to view full details.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex flex-wrap justify-center gap-2">
          {serviceCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => {
                setActiveCategory(category.id);
                setExpandedServiceId(null);
              }}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                activeCategory === category.id
                  ? "bg-[#111111] text-white"
                  : "bg-white text-[#111111] hover:bg-gray-100"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </section>

      <section data-testid="booking-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="space-y-3">
          {activeServices.length === 0 ? (
            <div className="glass-card p-8 text-center text-[#666]">
              No services added in this category yet.
            </div>
          ) : (
            activeServices.map((service) => {
              const expanded = expandedServiceId === service.id;
              return (
                <div key={service.id} className="glass-card p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h3 className="font-medium text-lg mb-1">{service.name}</h3>
                      <div className="flex items-center gap-4 text-sm text-[#666]">
                        <span className="font-medium text-[#111]">{service.price}</span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} /> {service.duration}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setExpandedServiceId(expanded ? null : service.id)}
                        className="px-3 py-2 rounded-full border border-[#ddd] text-sm text-[#444] hover:bg-[#f7f7f7] inline-flex items-center gap-1"
                      >
                        {expanded ? "Hide details" : "View details"} <ChevronDown size={14} className={expanded ? "rotate-180" : ""} />
                      </button>
                      <button
                        data-testid={`booking-service-${service.id}`}
                        onClick={() => openBooking(service.id)}
                        className="btn-black"
                      >
                        Select
                      </button>
                    </div>
                  </div>
                  {expanded && (
                    <div className="mt-4 pt-4 border-t border-[#ececec]">
                      <p className="text-sm text-[#666] whitespace-pre-line">
                        {service.details || "No additional details provided."}
                      </p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      <BookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        preSelectedService={selectedServiceId}
      />
    </div>
  );
}
